import { useCallback, useEffect, useMemo, useState } from "react";
import { runOptimisticBulkActionItemStatusChange } from "./optimisticActionItemStatus";

/**
 * Multi-select for bulk actions. Done checkboxes stay separate; selection is toggled via row clicks.
 *
 * @example
 * const bulk = useActionItemBulkSelect(visibleItems, handleStatusChange, getItemStableId);
 * // bulk.selectedCount > 0 → show bulk bar; bulk.toggleItemSelection(item) on row click
 */
export const useActionItemBulkSelect = (
  visibleItems,
  onStatusChange,
  getStableId,
  bulkStatusOptions = null
) => {
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());

  const visibleItemIds = useMemo(
    () => (Array.isArray(visibleItems) ? visibleItems : []).map((item) => getStableId(item)),
    [getStableId, visibleItems]
  );

  const selectedCount = selectedItemIds.size;
  const allVisibleSelected =
    visibleItemIds.length > 0 && visibleItemIds.every((id) => selectedItemIds.has(id));
  const someVisibleSelected =
    visibleItemIds.some((id) => selectedItemIds.has(id)) && !allVisibleSelected;

  useEffect(() => {
    setSelectedItemIds((previous) => {
      const visible = new Set(visibleItemIds);
      const next = new Set([...previous].filter((id) => visible.has(id)));
      return next.size === previous.size ? previous : next;
    });
  }, [visibleItemIds]);

  const clearSelection = useCallback(() => {
    setSelectedItemIds(new Set());
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    setSelectedItemIds(allVisibleSelected ? new Set() : new Set(visibleItemIds));
  }, [allVisibleSelected, visibleItemIds]);

  const toggleItemSelection = useCallback(
    (item) => {
      const stableId = getStableId(item);
      setSelectedItemIds((previous) => {
        const next = new Set(previous);
        if (next.has(stableId)) next.delete(stableId);
        else next.add(stableId);
        return next;
      });
    },
    [getStableId]
  );

  const isItemSelected = useCallback(
    (item) => selectedItemIds.has(getStableId(item)),
    [getStableId, selectedItemIds]
  );

  const getSelectedItems = useCallback(
    () =>
      (Array.isArray(visibleItems) ? visibleItems : []).filter((item) =>
        selectedItemIds.has(getStableId(item))
      ),
    [getStableId, selectedItemIds, visibleItems]
  );

  const runBulkStatusChange = useCallback(
    (nextStatus) => {
      const targets = getSelectedItems();
      if (targets.length === 0) return;

      setSelectedItemIds(new Set());
      if (bulkStatusOptions) {
        void runOptimisticBulkActionItemStatusChange({
          targets,
          newStatus: nextStatus,
          ...bulkStatusOptions,
          getStableId,
        });
        return;
      }
      targets.forEach((item) => {
        void onStatusChange(item, nextStatus);
      });
    },
    [bulkStatusOptions, getSelectedItems, getStableId, onStatusChange]
  );

  const handleBulkArchive = useCallback(() => {
    void runBulkStatusChange("ignore");
  }, [runBulkStatusChange]);

  const handleBulkDone = useCallback(() => {
    void runBulkStatusChange("done");
  }, [runBulkStatusChange]);

  return {
    selectedCount,
    allVisibleSelected,
    someVisibleSelected,
    handleToggleSelectAll,
    clearSelection,
    toggleItemSelection,
    isItemSelected,
    getSelectedItems,
    handleBulkArchive,
    handleBulkDone,
  };
};
